import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import { debounce } from 'lodash'

import TabStore from 'stores/tab/TabStore'
import TelemetryStore from 'stores/telemetry/TelemetryStore'

import Tooltip from 'components/tooltip/Tooltip'
import Input from 'components/input/Input'
import Mousetrap from 'components/mousetrap/Mousetrap'

import styles from './paginationFilter.scss'
import commonStyles from './common.scss'

interface IProps {}

interface IState {
  isOpen: boolean
}

class PaginationFilter extends React.PureComponent<IProps, IState> {
  private button: React.RefObject<HTMLDivElement> = React.createRef()
  private tooltipBody: React.RefObject<HTMLDivElement> = React.createRef()

  private runScriptDebounced = debounce(
    () => {
      const script = TabStore.activeTab?.session!.script!
      script.run()
    },
    300,
    {
      leading: false,
      trailing: true,
    }
  )

  state: IState = {
    isOpen: false,
  }

  handleToggle = () => {
    this.setState(state => ({
      isOpen: !state.isOpen,
    }))
  }

  handleChangeTake = (take: string) => {
    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!
    script.update({ frozen: false, pagination: { take: Number(take) } })
    this.runScriptDebounced()

    TelemetryStore.send({
      command: 'pagination_change',
      commandDetails: {
        take_change: true,
        skip_change: false,
      },
    })
  }

  handleChangeSkip = (skip: string) => {
    TabStore.activeTab?.update({ preview: false })

    const skipAsNumber = Number(skip)
    if (isNaN(skipAsNumber) || skipAsNumber < 0) {
      return
    }

    const script = TabStore.activeTab?.session!.script!
    script.update({
      frozen: false,
      pagination: {
        skip: skipAsNumber,
      },
    })
    this.runScriptDebounced()

    TelemetryStore.send({
      command: 'pagination_change',
      commandDetails: {
        take_change: false,
        skip_change: true,
      },
    })
  }

  render() {
    const { isOpen } = this.state
    const { recordIds, model, pagination } = TabStore.activeTab!.session!.script

    return (
      <div className={commonStyles.container}>
        <div
          data-cy="page-filter"
          ref={this.button}
          className={cn(commonStyles.pill, { [commonStyles.open]: isOpen })}
          onClick={this.handleToggle}
        >
          <span className={commonStyles.label}> Showing </span>
          <span>
            <span className={styles.noPadding} data-cy="pagination__take">
              {recordIds.length}
            </span>
            &nbsp;of&nbsp;
            <span className={styles.noPadding} data-cy="pagination__total">
              {model.count ?? '?'}
            </span>
          </span>
        </div>

        {isOpen && (
          <Tooltip
            className={styles.tooltip}
            target={this.button}
            onClickOutside={this.handleToggle}
          >
            <div className={styles.tooltipBody} ref={this.tooltipBody}>
              <span className={styles.text}>Take</span>
              <Input
                data-cy="pagination__take-input"
                type="number"
                tabIndex={0}
                focusOnMount
                className={styles.input}
                value={String(pagination.take)}
                onChange={this.handleChangeTake}
              />

              <div className={styles.separator} />

              <span className={styles.text}>Skip</span>
              <Input
                data-cy="pagination__skip-input"
                type="number"
                tabIndex={0}
                focusOnMount
                className={styles.input}
                value={String(pagination.skip)}
                onChange={this.handleChangeSkip}
              />
            </div>
          </Tooltip>
        )}

        {isOpen && (
          <Mousetrap
            keys="esc"
            target={this.tooltipBody}
            onMatch={() => this.handleToggle()}
          />
        )}
      </div>
    )
  }
}

export default observer(PaginationFilter)
